import React, { useEffect, useState } from "react";
import { Button, Form, Modal, Table, Badge, Alert } from "react-bootstrap";
import {
  getTenantUsers,
  assignUserToProject,
  getProjectAssignments
} from "../../api/api";
import { getErrorMessage } from "../../utils/errors";

const ROLES = ["sme", "qa", "media", "pr", "id"];
const ROLE_LABELS = {
  sme: "SME (Subject Matter Expert)",
  id: "Instructional Designer",
  qa: "Quality Assurance",
  media: "Media",
  pr: "Publishing"
};

const ProjectAssignmentModal = ({ show, onHide, tenantSlug, project }) => {
  const [users, setUsers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [role, setRole] = useState("sme");
  const [userId, setUserId] = useState("");
  const [error, setError] = useState("");
  const eligibleUsers = users.filter(
    (u) => u.role === role
  );
  const existingAssignmentForRole = assignments.find(
    (a) => a.role === role
  );

  const loadData = async () => {
    try {
      const [uRes, aRes] = await Promise.all([
        getTenantUsers(tenantSlug),
        getProjectAssignments(tenantSlug, project.id)
      ]);
      setUsers(uRes.data.filter(u => u.status === "active"));
      setAssignments(aRes.data);
    } catch (e) {
      setError("Failed to load assignments");
    }
  };

  useEffect(() => {
    if (show) loadData();
  }, [show]);

  const handleAssign = async () => {
    if (!userId) return;
    if (existingAssignmentForRole) {
      const ok = window.confirm(
      `Replace ${existingAssignmentForRole.email} as ${role.toUpperCase()}?`
      );
      if (!ok) return;
    }
    try {
      await assignUserToProject(tenantSlug, project.id, {
        user_id: Number(userId),
        role
      });
      setUserId("");
      loadData();
    } catch (e) {
      setError(getErrorMessage(e, "Assignment failed"));
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          Assign Users — <small>{project.name}</small>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && <Alert variant="danger" role="alert">{error}</Alert>}

        {/* Assign Form */}
        <div className="d-flex gap-2 mb-3">
          <Form.Select value={role} onChange={e => setRole(e.target.value)}>
            {ROLES.map(r => (
              <option key={r} value={r}>{ROLE_LABELS[r]}</option>
            ))}
          </Form.Select>

          <Form.Select value={userId} onChange={e => setUserId(e.target.value)}>
            <option value="">Select {role.toUpperCase()}</option>
            {eligibleUsers.map(u => (
              <option key={u.id} value={u.id}>
                {u.email}
              </option>
            ))}
          </Form.Select>
          {eligibleUsers.length === 0 && (
            <div className="text-muted mt-1" style={{ fontSize: 12 }}>
            No active users available for role <strong>{role.toUpperCase()}</strong>
            </div>
          )}

          {existingAssignmentForRole && (
            <Alert variant="warning" className="py-2" role="alert">
            This role is currently assigned to <strong>{existingAssignmentForRole.email}</strong>.
            Assigning a new user will replace the existing assignment.
            </Alert>
          )}

          <Button onClick={handleAssign}>Assign</Button>
        </div>

        {/* Existing assignments */}
        <Table bordered size="sm">
          <thead className="table-light">
            <tr>
              <th>Email</th>
              <th scope="col" style={{ width: 120 }}>Role</th>
            </tr>
          </thead>
          <tbody>
            {assignments.length === 0 ? (
              <tr>
                <td colSpan={2} className="text-muted text-center">
                  No assignments yet
                </td>
              </tr>
            ) : (
              assignments.map(a => (
                <tr key={a.id}>
                  <td>{a.email}</td>
                  <td>
                    <Badge bg="info">{a.role}</Badge>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ProjectAssignmentModal;
